import React, { useState, useEffect, useRef } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Presentation, LogOut, ShieldCheck, Globe, Menu, X, User, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { UserRole } from '../types';
import { motion, AnimatePresence } from 'framer-motion';
import { TreeWatermark } from './ui/TreeWatermark';
import { ScopeSwitcher } from './ScopeSwitcher';
import { ScopeFirstChoice } from './ScopeFirstChoice';
import { useCourse } from '../context/CourseContext';
import { OfflineBanner } from './OfflineBanner';
import { applyTenantBranding, clearTenantBranding } from '../lib/tenantBranding';
import { getDefaultLanding } from '../lib/defaultLanding';

interface AppLayoutProps {
  pageTitle?: string;
}

export const AppLayout: React.FC<AppLayoutProps> = ({ pageTitle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { tenant, features } = useTenant();
  // Unscoped, so the switch's counts say what's under each curriculum rather
  // than what survives the one currently selected.
  const { allCourses } = useCourse();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  // Apply tenant branding — overrides Tailwind --color-primary-* variables.
  // Staff users (admin + instructor) get an indigo palette instead, so admin
  // chrome reads as a consistent product regardless of tenant (see DECISIONS.md).
  useEffect(() => {
    const isStaff = user?.role === UserRole.INSTRUCTOR || user?.role === UserRole.ADMIN;
    if (isStaff) {
      applyTenantBranding('#1E3A8A', '#1E3A8A', '#818CF8', '#818CF8');
    } else if (tenant?.branding) {
      applyTenantBranding(
        tenant.branding.primaryColor,
        tenant.branding.secondaryColor,
        tenant.branding.primaryColorDark,
        tenant.branding.secondaryColorDark,
      );
    }
    const tenantName = tenant?.name && tenant.name !== 'Default' ? tenant.name : 'Kula Learn';
    document.title = pageTitle ? `${pageTitle} | ${tenantName}` : `${tenantName} | Learning`;
    return () => clearTenantBranding();
  }, [tenant, pageTitle, user?.role]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setIsProfileMenuOpen(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Close profile dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };
    if (isProfileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isProfileMenuOpen]);

  const isActive = (path: string) => location.pathname === path;
  const isActivePrefix = (prefix: string) => location.pathname.startsWith(prefix);

  // Clicking a nav link doesn't always trigger a scroll: React Router keeps
  // scroll position on pathname changes, and clicking the same URL is a no-op.
  // Scroll manually — to the hash target when the link has one, otherwise to
  // the top whenever we're already on that path.
  const handleNavClick = (to: string) => {
    const [path, hash] = to.split('#');
    if (path !== location.pathname) return;
    if (hash) {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (!user) return null;

  // The warm-paper "Blend" canvas is student-only; staff keep the neutral grey shell.
  const isStudent = user.role === UserRole.STUDENT;

  /**
   * Brand mark for the (light) student shell. A tenant's default logo is often a
   * white mark made for dark heroes, which vanishes on this white header — so we
   * only use an explicit light-surface logo, falling back to a clean wordmark of
   * the tenant name. Guarantees a visible brand mark for every tenant.
   */
  const renderBrand = (imgClass: string, textClass: string) => {
    if (tenant?.logoUrlLight) {
      return <img src={tenant.logoUrlLight} alt={tenant?.name || 'Kula Learn'} className={imgClass} />;
    }
    if (!tenant) {
      return <img src="/kula-logo.svg" alt="Kula Learn" className={imgClass} />;
    }
    return (
      <span className={`font-serif font-bold leading-tight tracking-tight truncate ${textClass}`}>
        {tenant.branding?.schoolName || tenant.name}
      </span>
    );
  };

  // The brand mark means "take me home", and inside this shell the user is always
  // signed in (see the `!user` guard above) — so it resolves to their own home,
  // never to `/`, which is the tenant's public face.
  const homeLink = getDefaultLanding(user.role);

  // Build nav links based on role
  const navLinks: { to: string; label: string; icon?: React.ReactNode; active: boolean }[] = [];
  if (user.role === UserRole.STUDENT) {
    navLinks.push({ to: '/browse', label: 'Home', active: isActive('/browse') });
    navLinks.push({ to: '/catalog', label: 'Courses', active: isActive('/catalog') });
    if (features.studentGradebook) {
      navLinks.push({ to: '/gradebook', label: 'Progress', active: isActive('/gradebook') });
    }
  }
  if (user.role === UserRole.INSTRUCTOR) {
    navLinks.push({ to: '/instructor', label: 'Instructor', icon: <Presentation className="h-4 w-4" />, active: isActivePrefix('/instructor') });
  }
  if (user.role === UserRole.ADMIN) {
    navLinks.push({ to: '/admin', label: 'Admin', icon: <ShieldCheck className="h-4 w-4" />, active: isActivePrefix('/admin') && !isActivePrefix('/admin/tenant') });
    navLinks.push({ to: '/instructor', label: 'Instructor', icon: <Presentation className="h-4 w-4" />, active: isActivePrefix('/instructor') });
    if (user.isSuperAdmin && tenant?.slug === 'hub') {
      navLinks.push({ to: '/hub', label: 'Hub', icon: <Globe className="h-4 w-4" />, active: isActivePrefix('/hub') });
    }
  }

  return (
    <div
      className={`min-h-screen flex flex-col font-sans ${isStudent ? 'bg-paper' : 'bg-gray-50 dark:bg-gray-950'}${
        /* Opt-in navy chrome (branding.appChrome). Applied as a scoped class
           rather than threaded through the navbar's ~150 lines of inline colour
           utilities — the repaint lives in one block in app.css, next to the
           rules it has to outrank. Content is untouched: this is nav + footer
           only. */
        tenant?.branding?.appChrome === 'dark' ? ' app-chrome-dark' : ''
      }`}
    >
      {/* Skip to content — accessibility */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[60] focus:bg-white focus:text-gray-900 focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg focus:text-sm focus:font-medium">
        Skip to content
      </a>

      <OfflineBanner />
      {/* Asked once, on a student's first arrival with nothing to go on — see
          the component. Renders nothing for staff, for a settled scope, or
          while only one curriculum has published courses. */}
      <ScopeFirstChoice courses={allCourses} />

      {/* Mobile menu overlay */}
      <div
        className={`fixed inset-0 z-50 md:hidden transition-opacity duration-300 ${isMobileMenuOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setIsMobileMenuOpen(false)}
        />
        {/* Drawer */}
        <div className={`absolute top-0 left-0 h-full w-72 bg-white dark:bg-gray-900 shadow-2xl transition-transform duration-300 ease-out ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-dark-border">
            {renderBrand('h-7 w-auto', 'text-base text-gray-900 max-w-[180px]')}
            <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg">
              <X className="h-5 w-5" />
            </button>
          </div>
          {/* Mobile user info */}
          <div className="px-4 py-4 border-b border-gray-100 dark:border-dark-border">
            <div className="flex items-center gap-3">
              {user.avatarUrl ? (
                <img src={user.avatarUrl} alt={user.name} className="h-10 w-10 rounded-lg object-cover bg-gray-200 dark:bg-gray-700" />
              ) : (
                <div className="h-10 w-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400 font-bold text-sm">
                  {user.name?.charAt(0)?.toUpperCase() || '?'}
                </div>
              )}
              <div>
                <p className="font-bold text-gray-900 dark:text-white text-sm">{user.name}</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">{user.role}</p>
              </div>
            </div>
          </div>
          {/* The scope switch, for the drawer. The chrome chip is `sm:` and up,
              and Revise's audience is phone-first — without this a student put
              into the wrong curriculum by the default or by inference has no
              way out on the device most of them are using. */}
          <ScopeSwitcher courses={allCourses} variant="bar" className="mx-3 mt-3" />

          {/* Mobile nav links */}
          <nav className="p-3 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => handleNavClick(link.to)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                  link.active
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                {link.icon}
                {link.label}
              </Link>
            ))}
          </nav>
          {/* Mobile account actions */}
          <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-gray-100 dark:border-dark-border space-y-1">
            <Link
              to="/change-password"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <Lock className="h-4 w-4" /> Change Password
            </Link>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <LogOut className="h-4 w-4" /> Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Navbar */}
      <header className={`sticky top-0 z-40 transition-all duration-300 border-b ${
        isStudent ? 'border-paper-line' : 'border-gray-100 dark:border-dark-border'
      } ${
        isStudent
          ? (scrolled ? 'bg-paper/85 backdrop-blur-md shadow-sm' : 'bg-paper')
          : (scrolled ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-sm' : 'bg-white dark:bg-gray-900')
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-10">
            {/* Mobile hamburger */}
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="md:hidden p-2 -ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg"
              aria-label="Open navigation menu"
            >
              <Menu className="h-5 w-5" />
            </button>

            <Link to={homeLink} className="flex items-center group transition-transform hover:scale-[1.03]">
              {renderBrand('h-8 w-auto', 'text-lg sm:text-xl text-gray-900 max-w-[200px] sm:max-w-xs')}
            </Link>

            <nav className="hidden md:flex items-center gap-8 text-sm font-semibold tracking-wide">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => handleNavClick(link.to)}
                  className={`relative transition-colors py-1 flex items-center gap-2 ${
                    link.active
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  {link.icon}
                  {link.label}
                  {link.active && (
                    <motion.div
                      layoutId="nav-indicator"
                      className="absolute -bottom-0.5 left-0 right-0 h-[2px] bg-primary-600 dark:bg-primary-400 rounded-full"
                      transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                    />
                  )}
                </Link>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-6">
            {/* The catalogue scope is app-wide, so its switch lives in the
                chrome rather than on the catalogue page. Renders nothing for
                staff or on tenants with no scope configured. */}
            <div className="hidden sm:block">
              <ScopeSwitcher courses={allCourses} />
            </div>
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                className="flex items-center gap-3 pl-4 border-l border-gray-100 dark:border-dark-border focus:outline-none"
              >
                <div className="hidden md:flex flex-col items-end">
                  <span className="text-sm font-bold text-gray-900 dark:text-white leading-none">{user.name}</span>
                  <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mt-1">{user.role}</span>
                </div>
                {user.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt={user.name}
                    className="h-10 w-10 rounded-lg object-cover bg-gray-200 dark:bg-gray-700 ring-2 ring-white dark:ring-gray-800 shadow-sm"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden'); }}
                  />
                ) : null}
                <div className={`h-10 w-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400 font-bold text-sm ring-2 ring-white dark:ring-gray-800 shadow-sm ${user.avatarUrl ? 'hidden' : ''}`}>
                  {user.name?.charAt(0)?.toUpperCase() || '?'}
                </div>
              </button>

              <AnimatePresence>
                {isProfileMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -4 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -4 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 mt-4 w-56 bg-white dark:bg-dark-elevated rounded-xl shadow-xl dark:shadow-dark-card border border-gray-100 dark:border-dark-border py-2 z-50"
                  >
                    <div className="px-4 py-3 border-b border-gray-50 dark:border-dark-border md:hidden">
                      <p className="font-bold text-gray-900 dark:text-white">{user.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{user.email}</p>
                    </div>
                    <div className="px-2">
                      <Link
                        to="/account"
                        onClick={() => setIsProfileMenuOpen(false)}
                        className="w-full text-left px-3 py-2 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-hover flex items-center gap-2 transition-colors font-medium"
                      >
                        <User className="h-4 w-4" /> Account Settings
                      </Link>
                      <Link
                        to="/change-password"
                        onClick={() => setIsProfileMenuOpen(false)}
                        className="w-full text-left px-3 py-2 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-hover flex items-center gap-2 transition-colors font-medium"
                      >
                        <Lock className="h-4 w-4" /> Change Password
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-3 py-2 rounded-lg text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 transition-colors font-medium"
                      >
                        <LogOut className="h-4 w-4" /> Sign Out
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main id="main-content" className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className={`border-t py-12 mt-auto relative overflow-hidden ${isStudent ? 'bg-paper border-paper-line' : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-dark-border'}`}>
        {/* Tree watermark */}
        <TreeWatermark
          className="absolute"
          style={{
            right: '5%',
            bottom: '-30%',
            width: 160,
            height: 160,
            opacity: 0.025,
          }}
          color={tenant?.branding?.primaryColor || 'var(--color-primary-600)'}
          animate={false}
        />
        <div className="max-w-7xl mx-auto px-4 text-center relative z-10">
            <div className="w-10 h-px bg-primary-200 dark:bg-primary-800 mx-auto mb-6" />
            <div className="flex items-center justify-center gap-2 mb-4">
              {renderBrand('h-7 w-auto opacity-80', 'text-base text-gray-400 max-w-[240px]')}
            </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            &copy; {new Date().getFullYear()} {tenant?.name || 'Kula Learn'}.{' '}
            <span className="font-serif italic">Powered by Kula Learn.</span>
          </p>
          <div className="flex items-center justify-center gap-4 mt-4">
            <Link to="/privacy" className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">Terms of Service</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};
